/**
 * WebMobileOnlyNotice — shown to signed-in web users who hit a
 * consumer-only route (/cellar, /add, /wine/*, /sommelier, etc.).
 *
 * Phase 4 of the web/app split — see
 * docs/strategy/web-app-split-2026-05-25.md. After this phase,
 * Vinarai's consumer experience lives in the iPhone app only;
 * vinarai.com keeps marketing + merchant + admin surfaces.
 *
 * The notice acknowledges the user is signed in (so they know
 * they're not being treated as a stranger) and routes them to
 * the iPhone app for the actual experience. Merchant + admin
 * users still have their dashboard links surfaced here so they
 * can pivot to the role that DOES work on web.
 */

import { useNavigate } from 'react-router-dom'
import { signOut } from 'firebase/auth'
import { auth } from '../firebase.js'

const C = {
  obsidian:  '#0E0C12',
  cellar:    '#3A0D18',
  burgundy:  '#7B2040',
  gold:      '#C4965A',
  goldSoft:  '#E0BC85',
  ivory:     '#F4EFE7',
  ivoryDim:  'rgba(244,239,231,0.78)',
  ivoryFaint:'rgba(244,239,231,0.50)',
  border:    'rgba(196,150,90,0.22)',
}
const FONTS = {
  serif: '"Cormorant Garamond", Garamond, serif',
  sans:  '"Raleway", "Helvetica Neue", system-ui, sans-serif',
}

export default function WebMobileOnlyNotice({
  user,
  // If true, show a "Continue to merchant dashboard" CTA. Caller
  // decides based on whether the user has merchant entitlements
  // — App.jsx has the context (accountType, isAdmin, etc.).
  showMerchantCTA = false,
  // Same for admin.
  showAdminCTA = false,
}) {
  const navigate = useNavigate()

  const handleSignOut = async () => {
    try { await signOut(auth) } catch (_) { /* non-fatal */ }
    // Land them back on the public landing
    navigate('/', { replace: true })
  }

  const firstName = (user?.displayName || '').trim().split(/\s+/)[0] || null

  return (
    <div style={{
      minHeight: '100vh',
      background: `linear-gradient(180deg, ${C.obsidian} 0%, ${C.cellar} 100%)`,
      color: C.ivory,
      fontFamily: FONTS.sans,
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      padding: '40px 24px',
    }}>
      <div style={{
        maxWidth: 480,
        textAlign: 'center',
        animation: 'fadeUp 0.6s ease both',
      }}>
        <style>{`@keyframes fadeUp { from { opacity: 0; transform: translateY(12px) } to { opacity: 1; transform: translateY(0) } }`}</style>

        {/* Eyebrow */}
        <p style={{
          fontFamily: FONTS.sans,
          fontSize: 10, fontWeight: 700,
          letterSpacing: '0.24em', textTransform: 'uppercase',
          color: C.gold,
          margin: '0 0 12px',
        }}>
          Vinarai is on iPhone
        </p>

        {/* Headline */}
        <h1 style={{
          fontFamily: FONTS.serif,
          fontSize: 36, fontWeight: 500,
          color: C.ivory,
          margin: '0 0 16px',
          lineHeight: 1.15,
        }}>
          {firstName ? `Hi ${firstName} — ` : ''}<span style={{ fontStyle: 'italic', color: C.goldSoft }}>your cellar lives in the app.</span>
        </h1>

        {/* Sub copy */}
        <p style={{
          fontFamily: FONTS.sans,
          fontSize: 14,
          color: C.ivoryDim,
          margin: '0 auto 28px',
          maxWidth: 380,
          lineHeight: 1.6,
        }}>
          Vinarai's consumer experience — your cellar, AI sommelier,
          friend cellars, tastings — runs on the iPhone app. Install it
          to pick up where you left off.
        </p>

        {/* Primary CTA */}
        <button
          onClick={() => navigate('/')}
          style={{
            background: C.gold,
            color: C.obsidian,
            fontFamily: FONTS.sans,
            fontSize: 13, fontWeight: 700,
            letterSpacing: '0.14em', textTransform: 'uppercase',
            padding: '14px 28px',
            borderRadius: 999,
            border: 'none',
            cursor: 'pointer',
            boxShadow: '0 8px 24px rgba(196,150,90,0.28)',
            marginBottom: 14,
          }}
        >
          Get the Vinarai App →
        </button>

        {/* Secondary CTAs — only render the relevant ones based on
            the user's roles. Admins + merchants still have valid
            web destinations; consumer-only users just get sign-out. */}
        <div style={{
          marginTop: 24,
          display: 'flex',
          flexDirection: 'column',
          gap: 10,
          alignItems: 'center',
        }}>
          {showMerchantCTA && (
            <button
              onClick={() => navigate('/merchant')}
              style={secondaryBtnStyle}
            >
              Continue to Merchant Console →
            </button>
          )}
          {showAdminCTA && (
            <button
              onClick={() => navigate('/admin')}
              style={secondaryBtnStyle}
            >
              Continue to Admin Panel →
            </button>
          )}
          <button
            onClick={handleSignOut}
            style={{
              background: 'transparent',
              color: C.ivoryFaint,
              fontFamily: FONTS.sans,
              fontSize: 12,
              letterSpacing: '0.06em',
              border: 'none',
              cursor: 'pointer',
              padding: 8,
              textDecoration: 'underline',
              textUnderlineOffset: 3,
            }}
          >
            Sign out
          </button>
        </div>
      </div>
    </div>
  )
}

const secondaryBtnStyle = {
  background: 'transparent',
  color: C.gold,
  fontFamily: FONTS.sans,
  fontSize: 12, fontWeight: 600,
  letterSpacing: '0.1em', textTransform: 'uppercase',
  padding: '10px 22px',
  borderRadius: 999,
  border: `1px solid ${C.border}`,
  cursor: 'pointer',
}
